import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FlaskConical, Eye, Loader2, ShieldAlert, UserCog, HardHat, UserSearch, StopCircle } from "lucide-react";
import { API, authHeaders } from "@/constants/roles";

const ROLE_CARDS = [
  {
    role: "manager",
    icon: UserCog,
    title: "Production Manager",
    desc: "See exactly what your manager sees — nav, drafts, reviews and tools limited by the permissions you set in Settings.",
    testid: "viewas-start-manager",
  },
  {
    role: "worker",
    icon: HardHat,
    title: "Worker (Standard Animator)",
    desc: "Experience the worker dashboard: available jobs, claiming, submissions, earnings and onboarding.",
    testid: "viewas-start-worker",
  },
  {
    role: "applicant",
    icon: UserSearch,
    title: "Applicant",
    desc: "See the applicant status view exactly as a candidate sees it after applying.",
    testid: "viewas-start-applicant",
  },
];

export const AdminTestMode = () => {
  const [active, setActive] = useState(undefined);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/admin/viewas`, { headers: authHeaders() })
      .then((r) => setActive(r.data.session || null))
      .catch(() => setActive(null));
  }, []);

  useEffect(load, [load]);

  const start = async (role) => {
    setBusy(role);
    try {
      const r = await axios.post(`${API}/admin/viewas/start`, { role }, { headers: authHeaders() });
      const ownerToken = localStorage.getItem("melto_token");
      localStorage.setItem("melto_token_owner", ownerToken);
      localStorage.setItem("melto_token", r.data.token);
      localStorage.setItem("melto_viewas", JSON.stringify({ session_id: r.data.session_id, role: r.data.role, started_at: r.data.started_at }));
      toast.success(`Test session started — viewing as ${role}.`);
      window.location.href = "/dashboard";
    } catch (err) {
      toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Could not start test session.");
      setBusy(null);
    }
  };

  const stop = async () => {
    setBusy("stop");
    try {
      const r = await axios.post(`${API}/admin/viewas/stop`, {}, { headers: authHeaders() });
      toast.success(`Test session ended — ${r.data.restored} record(s) restored, ${r.data.purged_tagged + r.data.purged_owned} test record(s) purged.`);
      setActive(null);
    } catch {
      toast.error("Could not stop the session.");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="testmode-section">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-500/15 text-amber-300">
          <FlaskConical className="h-5 w-5" />
        </div>
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-[var(--pv-text)]">View As / Test Mode</h1>
          <p className="mt-1 text-sm text-[var(--pv-text-4)]">Step into any role with a sandbox account — safe for live production data.</p>
        </div>
      </div>

      <div className="mt-5 flex items-start gap-3 rounded-2xl border border-amber-500/25 bg-amber-500/10 px-5 py-4">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-300" />
        <p className="text-xs leading-relaxed text-amber-200/90">
          Everything you do in a test session is <b>sandboxed</b>: records created are tagged and purged when you exit, changes to existing
          records are restored, and <b>no emails, notifications, payments or other external actions ever fire</b>. A permanent banner stays
          on screen for the whole session.
        </p>
      </div>

      {active && (
        <div data-testid="viewas-active-card" className="mt-6 flex flex-wrap items-center justify-between gap-3 rounded-3xl border border-amber-500/30 bg-amber-500/10 p-6">
          <div>
            <p className="text-sm font-bold text-amber-200">Session active — viewing as {active.role}</p>
            <p className="mt-0.5 text-xs text-amber-200/70">Started {new Date(active.started_at).toLocaleString()} · sandbox account {active.sandbox_email}</p>
          </div>
          <button data-testid="viewas-stop-btn" onClick={stop} disabled={busy === "stop"} className="flex items-center gap-1.5 rounded-full bg-amber-500 px-4 py-2 text-xs font-bold text-amber-950 hover:bg-amber-400 disabled:opacity-50">
            {busy === "stop" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <StopCircle className="h-3.5 w-3.5" />} Stop &amp; purge test data
          </button>
        </div>
      )}

      <div className="mt-6 grid gap-4 md:grid-cols-3">
        {ROLE_CARDS.map(({ role, icon: Icon, title, desc, testid }) => (
          <div key={role} data-testid={`viewas-card-${role}`} className="flex flex-col rounded-3xl border border-[var(--pv-border)] bg-[var(--pv-card)] p-6">
            <Icon className="h-6 w-6 text-cyan-400" />
            <p className="mt-3 font-display text-lg font-bold text-[var(--pv-text)]">{title}</p>
            <p className="mt-1.5 flex-1 text-xs leading-relaxed text-[var(--pv-text-4)]">{desc}</p>
            <button
              data-testid={testid}
              onClick={() => start(role)}
              disabled={busy !== null}
              className="mt-5 flex w-fit items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-xs font-bold text-white transition-colors hover:bg-cyan-600 disabled:opacity-50"
            >
              {busy === role ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
              Start test session
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
